import { useEffect, useMemo, useState } from 'react';
import { AlarmClock, Plus, LogOut, Search, Sparkles, User } from 'lucide-react';
import { ACTIVE_STAGES, TERMINAL_STAGES, type Lead, type LeadSource, type LeadStage, type LeadTask, type Sector } from '../types/lead.types';
import { STAGE_LABELS, SECTOR_OPTIONS, SOURCE_OPTIONS, SOURCING_SECTOR_OPTIONS } from '../config/leadTaxonomy';
import {
  canSetStageDirectly, computePipelineStats, filterLeads, runLeadSourcing, setStage, setTaskDone,
  subscribeToLeads, subscribeToOpenTasks, type LeadFilters, type SourceLeadsResult,
} from '../services/leadService';
import { isDueToday, isOverdue } from '../utils/date';
import { signOut } from '../services/auth.service';
import { LeadCard } from '../components/LeadCard';
import { LeadFormModal } from '../components/LeadFormModal';
import { LeadDetailPanel } from '../components/LeadDetailPanel';
import { SourcingModal } from '../components/SourcingModal';

// Tutte le colonne, attive + terminali — un lead Vinto/Perso resta sempre
// raggiungibile (prima spariva dalla board senza modo di riaprirlo, bug
// reale: il link di onboarding generato alla conversione diventava
// irrecuperabile).
const ALL_STAGES = [...ACTIVE_STAGES, ...TERMINAL_STAGES];

interface Props {
  currentUid: string;
  arcanumLevel: string | null;
}

type FormState = { mode: 'create' } | { mode: 'edit'; lead: Lead } | null;

export function PipelinePage({ currentUid, arcanumLevel }: Props) {
  const [leads, setLeads] = useState<Lead[]>([]);
  const [openTasks, setOpenTasks] = useState<LeadTask[]>([]);
  const [formState, setFormState] = useState<FormState>(null);
  const [selected, setSelected] = useState<Lead | null>(null);
  const [autoOpenLostReason, setAutoOpenLostReason] = useState(false);
  const [dragOverStage, setDragOverStage] = useState<LeadStage | null>(null);
  const [search, setSearch] = useState('');
  // "Solo i miei" (proprietario) e "canale" erano 2 controlli separati ma
  // combinabili — fusi in un unico select (17/9/2026, richiesta esplicita):
  // sono alternative ('mine' XOR un canale), non più filtri indipendenti.
  const [attributionFilter, setAttributionFilter] = useState<'all' | 'mine' | LeadSource>('all');
  const [sectorFilter, setSectorFilter] = useState<Sector | ''>('');
  const [showSourcingModal, setShowSourcingModal] = useState(false);
  const [sourcingResult, setSourcingResult] = useState<SourceLeadsResult | null>(null);
  const [continuingSearch, setContinuingSearch] = useState(false);

  // Riprende una ricerca "capped" da dove si era fermata (v. resumeToken) —
  // serve soprattutto per le scansioni su tutta la Svizzera, dove una parola
  // chiave generica può avere migliaia di aziende candidate e un giro solo
  // non basta (18/9/2026, introdotto dopo la prima scansione nazionale
  // Fiduciario: 200 creati, 33 duplicati, ancora "capped" a fine giro).
  const handleContinueSearch = async () => {
    if (!sourcingResult?.resumeToken) return;
    setContinuingSearch(true);
    try {
      const result = await runLeadSourcing({
        sector: sourcingResult.sector,
        cantons: sourcingResult.cantons,
        cities: sourcingResult.cities,
        postalCode: sourcingResult.postalCode,
        resumeFrom: sourcingResult.resumeToken,
      });
      setSourcingResult(result);
    } catch (err) {
      console.error('[PipelinePage] continua ricerca fallita', err);
    } finally {
      setContinuingSearch(false);
    }
  };

  useEffect(() => subscribeToLeads(setLeads), []);
  useEffect(() => subscribeToOpenTasks(setOpenTasks), []);

  // Tiene il pannello di dettaglio sincronizzato con gli aggiornamenti realtime.
  useEffect(() => {
    if (!selected) return;
    const fresh = leads.find((l) => l.id === selected.id);
    if (fresh) setSelected(fresh);
  }, [leads, selected]);

  // Un solo task per lead (il più vicino a scadere, dato l'order by dueAt
  // asc della query) — basta la prima occorrenza per leadId.
  const nextTaskByLead = useMemo(() => {
    const map = new Map<string, LeadTask>();
    for (const task of openTasks) {
      if (!map.has(task.leadId)) map.set(task.leadId, task);
    }
    return map;
  }, [openTasks]);

  const leadsById = useMemo(() => new Map(leads.map((l) => [l.id, l])), [leads]);

  // Task scaduti o di oggi, su TUTTI i lead — non filtrato dai filtri board:
  // un'urgenza non deve sparire perché non corrisponde al filtro settore attivo.
  const dueTasksToday = useMemo(
    () => openTasks.filter((t) => isOverdue(t.dueAt) || isDueToday(t.dueAt)),
    [openTasks]
  );

  const onlyMine = attributionFilter === 'mine';
  const sourceFilter = attributionFilter === 'all' || attributionFilter === 'mine' ? '' : attributionFilter;
  const filteredLeads = useMemo(() => {
    const filters: LeadFilters = { search, onlyMine, currentUid, sector: sectorFilter, source: sourceFilter };
    return filterLeads(leads, filters);
  }, [leads, search, onlyMine, currentUid, sectorFilter, sourceFilter]);
  const stats = useMemo(() => computePipelineStats(filteredLeads), [filteredLeads]);

  const byStage = useMemo(() => {
    const map = new Map<string, Lead[]>();
    for (const stage of ALL_STAGES) map.set(stage, []);
    for (const lead of filteredLeads) {
      map.get(lead.stage)?.push(lead);
    }
    return map;
  }, [filteredLeads]);

  const handleEditFromDetail = (lead: Lead) => {
    setSelected(null);
    setFormState({ mode: 'edit', lead });
  };

  const openLead = (lead: Lead) => {
    setAutoOpenLostReason(false);
    setSelected(lead);
  };

  const closeDetail = () => {
    setSelected(null);
    setAutoOpenLostReason(false);
  };

  // Il drop su "Vinto"/"Perso" non cambia mai lo stage direttamente — sono
  // stati che richiedono un passaggio dedicato (conversione a mandato /
  // motivo di perdita obbligatorio, v. firestore.rules e canSetStageDirectly).
  // Il drop apre invece il dettaglio, pronto per quel passaggio.
  const handleDrop = (targetStage: LeadStage, leadId: string) => {
    const lead = leadsById.get(leadId);
    if (!lead || lead.stage === targetStage) return;

    if (targetStage === 'won') {
      openLead(lead);
      return;
    }
    if (targetStage === 'lost') {
      setSelected(lead);
      setAutoOpenLostReason(true);
      return;
    }
    if (!canSetStageDirectly(targetStage)) return;
    setStage(lead.id, targetStage).catch((err) => {
      console.error('[PipelinePage] spostamento stage fallito', err);
    });
  };

  return (
    <div className="h-screen flex flex-col overflow-hidden bg-gray-50">
      <header className="flex items-center justify-between px-6 py-4 bg-white border-b border-gray-200 shrink-0">
        <h1 className="text-lg font-bold text-navy-900">Arcanum CRM — Pipeline</h1>
        <div className="flex items-center gap-3">
          <button
            onClick={() => setShowSourcingModal(true)}
            title="Cerca nuove aziende candidate nel registro di commercio, un settore alla volta"
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold border border-navy-900 text-navy-900"
          >
            <Sparkles size={14} /> Cerca nuovi lead
          </button>
          <button
            onClick={() => setFormState({ mode: 'create' })}
            className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-sm font-semibold text-white bg-navy-900"
          >
            <Plus size={14} /> Nuovo lead
          </button>
          <button onClick={() => signOut()} className="text-gray-400 hover:text-gray-600">
            <LogOut size={16} />
          </button>
        </div>
      </header>

      {/* Esito dell'ultima ricerca — resta visibile finché non si chiude o
          non se ne lancia una nuova (azzerato in onDone/handleCloseSourcing). */}
      {sourcingResult && (
        <div className="flex items-center justify-between gap-3 px-6 py-2 border-b text-sm bg-green-50 border-green-100 text-green-800">
          <span>
            {SOURCING_SECTOR_OPTIONS.find((s) => s.id === sourcingResult.sector)?.label ?? sourcingResult.sector}
            {sourcingResult.cantons.length > 0 && ` · ${sourcingResult.cantons.join(', ')}`}
            {sourcingResult.cities && sourcingResult.cities.length > 0 && ` · ${sourcingResult.cities.join(', ')}`}
            {sourcingResult.postalCode && ` · CAP ${sourcingResult.postalCode}`}
            {' — '}
            {sourcingResult.leadsCreated} nuovi lead creati
            {sourcingResult.duplicatesSkipped > 0 && `, ${sourcingResult.duplicatesSkipped} già presenti`}
            {sourcingResult.capped && ' (limite per esecuzione raggiunto)'}.
          </span>
          <div className="flex items-center gap-3 shrink-0">
            {sourcingResult.resumeToken && (
              <button
                onClick={handleContinueSearch}
                disabled={continuingSearch}
                className="text-xs font-semibold underline disabled:opacity-50"
              >
                {continuingSearch ? 'Continuo…' : 'Continua la scansione'}
              </button>
            )}
            <button onClick={() => setSourcingResult(null)} className="text-xs underline">
              Chiudi
            </button>
          </div>
        </div>
      )}

      {/* Barra numeri — riflette i filtri attivi, non l'intera pipeline, così
          resta coerente con quello che si vede sulla board sotto. */}
      <div className="flex flex-wrap items-center gap-x-6 gap-y-1 px-6 py-2.5 bg-white border-b border-gray-100 text-xs shrink-0">
        <Stat label="Pipeline attiva" value={`CHF ${stats.activeValue.toLocaleString('it-CH')}`} />
        <Stat label="Lead attivi" value={String(stats.activeCount)} />
        <Stat label="Vinti" value={String(stats.wonCount)} />
        <Stat label="Persi" value={String(stats.lostCount)} />
        <Stat label="Win rate" value={stats.winRate == null ? '—' : `${Math.round(stats.winRate * 100)}%`} />
      </div>

      {/* Da fare oggi — indipendente dai filtri board (v. dueTasksToday). */}
      {dueTasksToday.length > 0 && (
        <div className="flex items-center gap-2 px-6 py-2 bg-amber-50 border-b border-amber-100 overflow-x-auto shrink-0">
          <AlarmClock size={14} className="text-amber-600 shrink-0" />
          {dueTasksToday.map((task) => {
            const lead = leadsById.get(task.leadId);
            if (!lead) return null;
            const overdue = isOverdue(task.dueAt);
            return (
              <div
                key={task.id}
                role="button"
                tabIndex={0}
                onClick={() => openLead(lead)}
                onKeyDown={(e) => { if (e.key === 'Enter') openLead(lead); }}
                className={`flex items-center gap-1.5 px-2 py-1 rounded-lg text-xs shrink-0 border cursor-pointer ${
                  overdue ? 'border-red-200 bg-red-50 text-red-700' : 'border-amber-200 bg-amber-100/60 text-amber-800'
                }`}
              >
                <span className="font-semibold truncate max-w-[10rem]">{lead.organizationName}</span>
                <span className="truncate max-w-[12rem]">{task.text}</span>
                <button
                  onClick={(e) => { e.stopPropagation(); setTaskDone(task.leadId, task.id, true); }}
                  className="ml-1 underline decoration-dotted shrink-0"
                >
                  Fatto
                </button>
              </div>
            );
          })}
        </div>
      )}

      {/* Filtri board */}
      <div className="flex flex-wrap items-center gap-2 px-6 py-2.5 bg-white border-b border-gray-100 shrink-0">
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600 min-w-0">
          <Search size={13} className="shrink-0 text-gray-400" />
          <input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Cerca azienda…"
            className="outline-none w-40 min-w-0"
          />
        </div>
        <div className="flex items-center gap-1.5 px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600">
          <User size={13} className="shrink-0 text-gray-400" />
          <select
            value={attributionFilter}
            onChange={(e) => setAttributionFilter(e.target.value as 'all' | 'mine' | LeadSource)}
            className="outline-none bg-transparent"
          >
            <option value="all">Tutti i lead</option>
            <option value="mine">Solo i miei</option>
            <optgroup label="Canale">
              {SOURCE_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
            </optgroup>
          </select>
        </div>
        <select
          value={sectorFilter}
          onChange={(e) => setSectorFilter(e.target.value as Sector | '')}
          className="px-2.5 py-1.5 border border-gray-300 rounded-lg text-sm text-gray-600"
        >
          <option value="">Tutti i settori</option>
          {SECTOR_OPTIONS.map((s) => <option key={s.id} value={s.id}>{s.label}</option>)}
        </select>
        {(search || attributionFilter !== 'all' || sectorFilter) && (
          <button
            onClick={() => { setSearch(''); setAttributionFilter('all'); setSectorFilter(''); }}
            className="text-xs text-gray-400 hover:text-gray-600 underline"
          >
            Azzera filtri
          </button>
        )}
      </div>

      {/* La board occupa lo spazio restante ed è l'unica area che scrolla —
          orizzontalmente tra le colonne, verticalmente dentro ciascuna — la
          pagina in sé non scrolla mai. Colonne attive a larghezza flessibile
          (flex-1 con basis/min/max, min-w 18rem: più stretto tagliava i
          contenuti delle card, v. commit precedente). Vinto/Perso sono più
          strette e a larghezza fissa (18/9/2026, richiesta esplicita): sono
          gli stage terminali, guardati meno spesso in dettaglio, e restringerli
          libera spazio per le 5 colonne attive di lavoro quotidiano. */}
      <div className="flex-1 flex gap-3 px-4 py-4 overflow-x-auto min-h-0">
        {ALL_STAGES.map((stage) => {
          const isTerminalColumn = stage === 'won' || stage === 'lost';
          return (
            <div
              key={stage}
              onDragOver={(e) => { e.preventDefault(); setDragOverStage(stage); }}
              onDragLeave={() => setDragOverStage((s) => (s === stage ? null : s))}
              onDrop={(e) => {
                e.preventDefault();
                setDragOverStage(null);
                const leadId = e.dataTransfer.getData('text/plain');
                if (leadId) handleDrop(stage, leadId);
              }}
              className={`flex flex-col min-h-0 rounded-xl transition-colors ${
                isTerminalColumn ? 'w-56 shrink-0' : 'flex-1 basis-72 min-w-[18rem] max-w-[22rem]'
              } ${dragOverStage === stage ? 'bg-navy-50 ring-2 ring-navy-200' : ''}`}
            >
              <div className="flex items-center justify-between mb-2 shrink-0 px-0.5">
                <h2 className="text-sm font-semibold text-gray-600">{STAGE_LABELS[stage]}</h2>
                <span className="text-xs text-gray-400">{byStage.get(stage)?.length ?? 0}</span>
              </div>
              {isTerminalColumn && (
                <p className="text-[10px] text-gray-400 mb-1.5 px-0.5">
                  {stage === 'won' ? 'Si imposta da "Segna Vinto" nel dettaglio' : 'Trascina qui per scegliere il motivo'}
                </p>
              )}
              {/* flex-1 è la parte che mancava (bug reale trovato dal vivo
                  16/9/2026): senza, questo div non partecipa alla
                  distribuzione verticale dello spazio nella colonna
                  flex-col — si dimensiona sul proprio contenuto invece che
                  sullo spazio disponibile, e con molte card (dopo un giro
                  di sourcing) il contenuto trabocca facendo scrollare
                  l'intera pagina invece che solo questa lista. */}
              <div className="flex-1 space-y-2 overflow-y-auto min-h-0 pr-1">
                {byStage.get(stage)?.map((lead) => (
                  <LeadCard
                    key={lead.id}
                    lead={lead}
                    nextTask={nextTaskByLead.get(lead.id)}
                    onClick={() => openLead(lead)}
                  />
                ))}
              </div>
            </div>
          );
        })}
      </div>

      {showSourcingModal && (
        <SourcingModal
          onClose={() => setShowSourcingModal(false)}
          onDone={(result) => { setShowSourcingModal(false); setSourcingResult(result); }}
        />
      )}

      {formState && (
        <LeadFormModal
          createdBy={currentUid}
          lead={formState.mode === 'edit' ? formState.lead : undefined}
          onClose={() => setFormState(null)}
          onSaved={() => setFormState(null)}
        />
      )}

      {selected && (
        <LeadDetailPanel
          lead={selected}
          currentUid={currentUid}
          arcanumLevel={arcanumLevel}
          autoOpenLostReason={autoOpenLostReason}
          onClose={closeDetail}
          onEdit={handleEditFromDetail}
        />
      )}
    </div>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex items-baseline gap-1.5">
      <span className="text-gray-400 uppercase tracking-wide text-[10px]">{label}</span>
      <span className="font-semibold text-navy-900">{value}</span>
    </div>
  );
}
